import { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, API } from '../App';
import axios from 'axios';
import { Clock, CheckCircle, Loader } from 'lucide-react';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

export default function PaymentSuccess() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    handlePaymentReturn();
  }, []);

  const handlePaymentReturn = async () => {
    try {
      // Get token from URL
      const params = new URLSearchParams(location.search);
      const token = params.get('token'); // This is the PayPal order ID
      
      console.log('🎉 Payment return - OrderID:', token);

      if (!token) {
        throw new Error('No payment token found');
      }

      // Capture the payment
      console.log('📤 Capturing payment...');
      const response = await axios.post(
        `${API}/payments/capture?order_id=${token}`,
        {},
        { withCredentials: true }
      );

      console.log('✅ Payment captured:', response.data);

      // Refresh user data
      await checkAuth();

      // Show success message
      toast.success(t('notifications.paymentSuccess'), {
        duration: 5000
      });

      // Redirect to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/app');
      }, 2000);

    } catch (error) {
      console.error('❌ Error processing payment:', error);
      setError(error.response?.data?.detail || t('errors.paymentFailed'));
      
      toast.error(t('errors.paymentFailed'));
      
      // Redirect to payment page after 3 seconds
      setTimeout(() => {
        navigate('/payment-required');
      }, 3000);
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 via-white to-cyan-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <Clock className="h-12 w-12 text-teal-500" />
          <span className="text-3xl font-bold text-gray-900 ml-2">Chronos</span>
        </div>

        <div className="bg-white rounded-3xl shadow-xl p-8 text-center">
          {processing ? (
            <>
              <div className="mb-6">
                <Loader className="h-16 w-16 text-teal-500 animate-spin mx-auto" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Processing your payment...
              </h1>
              <p className="text-gray-600">
                Please wait while we confirm your payment with PayPal.
              </p>
            </>
          ) : error ? (
            <>
              <div className="mb-6">
                <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto">
                  <span className="text-4xl">❌</span>
                </div>
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Failed
              </h1>
              <p className="text-gray-600 mb-4">
                {error}
              </p>
              <p className="text-sm text-gray-500">
                Redirecting back to payment page...
              </p>
            </>
          ) : (
            <>
              <div className="mb-6">
                <CheckCircle className="h-16 w-16 text-green-500 mx-auto" />
              </div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Payment Successful! 🎉
              </h1>
              <p className="text-gray-600 mb-4">
                Welcome to Chronos Premium!
              </p>
              <div className="bg-teal-50 rounded-lg p-4 mb-4">
                <p className="text-teal-700 font-medium">
                  You now have full access to all features
                </p>
              </div>
              <p className="text-sm text-gray-500">
                Redirecting to dashboard...
              </p>
            </>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            Powered by PayPal • Secure Payment
          </p>
        </div>
      </div>
    </div>
  );
}
